import { eventPartOfSeriesShortString } from './PeekEventContent.locstring.json';
import { observer } from 'mobx-react-lite';
import { Icon } from '@fluentui/react/lib/Icon';
import DataRow from 'owa-calendar-forms-common/lib/components/DataRow';
import { AcceptProposalLink } from 'owa-accept-time-proposal-link';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import { eventDateAccessibility } from 'owa-locstrings/lib/strings/eventdateaccessibility.locstring.json';
import formatMeetingTimeSpan from 'owa-timeformat/lib/utils/formatMeetingTimeSpan';
import * as React from 'react';
import { TimeProposalsStub } from 'owa-calendar-forms-common/lib/components/componentStubs';
import { userDate } from 'owa-datetime';
import { logUsage } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ViewType } from 'owa-calendar-actions';

import styles from './CalendarItemPeek.scss';

interface PeekEventTimeRowProps {
    item: CalendarEvent;
    onReviewProposedNewTimeClicked?: (event?: Partial<CalendarEvent>) => void;
    actionSource: ViewType;
}

export default observer(function PeekEventTimeRow(props: PeekEventTimeRowProps) {
    const { item, onReviewProposedNewTimeClicked, actionSource } = props;
    const newTimeProposers =
        item.RequiredAttendees && item.OptionalAttendees
            ? item.RequiredAttendees.concat(item.OptionalAttendees).filter(
                  attendee => attendee.ProposedStart
              )
            : [];
    const shouldRenderAcceptLink =
        onReviewProposedNewTimeClicked &&
        isFeatureEnabled('mail-acceptProposedTimeLink') &&
        item.IsOrganizer &&
        (item.RequiredAttendees || item.OptionalAttendees) &&
        item.RequiredAttendees.concat(item.OptionalAttendees).length == 1 &&
        newTimeProposers.length != 0;

    const onReviewPNTClickedWrapper = React.useCallback((event?: Partial<CalendarEvent>) => {
        logUsage('TimeProposalInteraction', {
            interactionType: 'ReviewPNT',
            actionSource: actionSource,
        });
        onReviewProposedNewTimeClicked(event);
    }, []);

    return (
        <DataRow
            className={styles.timeRow}
            iconName={ControlIcons.Clock}
            iconClassName={styles.whenIcon}>
            <div className={styles.timeDataValue}>
                <span aria-label={loc(eventDateAccessibility)} className={styles.eventDate}>
                    {formatMeetingTimeSpan(item.Start, item.End, item.Recurrence)}
                </span>
                {item.IsRecurring && (
                    <div className={styles.partOfSeriesText}>
                        <span>{loc(eventPartOfSeriesShortString)}</span>
                        <Icon
                            iconName={ControlIcons.SyncOccurence}
                            styles={{
                                root: {
                                    fontSize: '14px',
                                    marginTop: '1px',
                                    marginLeft: '4px',
                                    userSelect: 'none',
                                },
                            }}
                        />
                    </div>
                )}
                {item.IsOrganizer && onReviewProposedNewTimeClicked && (
                    <TimeProposalsStub
                        event={item}
                        onReviewClicked={
                            shouldRenderAcceptLink
                                ? onReviewPNTClickedWrapper
                                : onReviewProposedNewTimeClicked
                        }
                        hideTime={true}
                        className={styles.timeProposals}
                    />
                )}
                {shouldRenderAcceptLink && (
                    <div className={styles.proposalRow}>
                        <span
                            aria-label={loc(eventDateAccessibility)}
                            className={styles.proposalContainer}>
                            {formatMeetingTimeSpan(
                                userDate(newTimeProposers[0].ProposedStart),
                                userDate(newTimeProposers[0].ProposedEnd),
                                item.Recurrence
                            )}
                        </span>
                        <AcceptProposalLink
                            proposedStart={userDate(newTimeProposers[0].ProposedStart)}
                            proposedEnd={userDate(newTimeProposers[0].ProposedEnd)}
                            calendarEvent={item}
                            actionSource={actionSource}
                            acceptLinkClassName={styles.acceptLink}
                            conflictDetailsClassName={styles.conflictDetails}
                        />
                    </div>
                )}
            </div>
        </DataRow>
    );
});
